import { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, ArrowRight, Check, ChevronRight, X } from 'lucide-react';

/**
 * The Setup Wizard walkthrough: real screenshots of this platform, arrowed and captioned,
 * grouped by area. It replaces the Zoom call the owner would otherwise have to run for every
 * new contributor. Images and text come from /guides/manifest.json, which is regenerated by
 * backend/scripts/walkthrough/capture.py — never hand-edit either.
 *
 * Areas are scoped by hub only, never by which features are switched on: a new vendor whose
 * storefront is still off is exactly the person who needs the storefront walkthrough.
 */

// His ordering, left column then right (session 70c). Anything not listed falls in after.
const LEFT = ['start', 'money', 'storefront'];
const RIGHT = ['workshops', 'bookings', 'forms'];

export default function WalkthroughModal({
  hub = 'practitioner',
  resume = null,
  onProgress = () => {},
  onDismiss = () => {},
  onFinish = () => {},
}) {
  const [cats, setCats] = useState([]);
  const [openCat, setOpenCat] = useState(resume?.category || null);
  const [i, setI] = useState(resume?.step || 0);
  const [done, setDone] = useState({});
  const onClose = onDismiss;

  useEffect(() => {
    fetch('/guides/manifest.json')
      .then((r) => r.json())
      .then((d) => setCats(d.categories || []))
      .catch(() => setCats([]));
  }, []);

  const visible = useMemo(() => cats.filter((c) => c.hubs.includes(hub)), [cats, hub]);

  // Two hand-ordered columns on desktop; on a phone they simply stack in the same order.
  const columns = useMemo(() => {
    const pick = (keys) => keys.map((k) => visible.find((c) => c.key === k)).filter(Boolean);
    const left = pick(LEFT);
    const right = pick(RIGHT);
    const rest = visible.filter((c) => ![...LEFT, ...RIGHT].includes(c.key));
    return right.length ? [left, [...right, ...rest]] : [[...left, ...rest]];
  }, [visible]);

  const cat = visible.find((c) => c.key === openCat) || null;
  const step = cat?.steps?.[i] || null;

  // Remember where they got to, so closing it is never a loss of place.
  useEffect(() => {
    if (openCat) onProgress(openCat, i);
  }, [openCat, i, onProgress]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
      if (!cat) return;
      if (e.key === 'ArrowRight') setI((v) => Math.min(v + 1, cat.steps.length - 1));
      if (e.key === 'ArrowLeft') setI((v) => Math.max(v - 1, 0));
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [cat, onClose]);

  const finishCat = () => {
    const seen = { ...done, [cat.key]: true };
    setDone(seen);
    setOpenCat(null);
    setI(0);
    onProgress(null, 0);
    // Reached the end of everything for this hub — stop opening on its own.
    if (visible.every((c) => seen[c.key])) onFinish();
  };

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center bg-black/80 p-3 sm:p-6"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      data-testid="walkthrough-modal"
    >
      <div className="relative w-full max-w-5xl max-h-[92vh] overflow-y-auto rounded-2xl border border-amber-500/25 bg-[#0f1526] p-5 sm:p-7 shadow-2xl">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 text-[#f5f0e8]/50 transition-colors duration-200 hover:text-[#f5f0e8]"
          data-testid="walkthrough-close"
        >
          <X className="h-5 w-5" />
        </button>

        {!cat ? (
          <>
            <p className="text-xs font-bold uppercase tracking-[1.5px] text-amber-400/80">
              Setup walkthroughs
            </p>
            <h2 className="mt-1 font-serif text-2xl text-[#f5f0e8]">
              How to set up each part of your Hub
            </h2>
            <p className="mt-2 max-w-2xl text-sm text-[#f5f0e8]/55">
              Pick an area. Each one is a short set of real screenshots of this platform with the
              buttons marked, so you can follow along in another tab. You can reopen these any
              time from <strong className="text-[#f5f0e8]/70">Setup Walkthroughs</strong> in your
              Quick Actions.
            </p>

            <div className="mt-6 flex flex-col gap-3 sm:flex-row" data-testid="walkthrough-categories">
              {columns.map((col, n) => (
                <div key={n} className="flex-1 space-y-3">
                  {col.map((c) => (
                    <button
                      key={c.key}
                      onClick={() => { setOpenCat(c.key); setI(0); }}
                      className="group flex w-full items-center gap-4 rounded-xl border border-white/10 bg-white/[0.03] p-4 text-left transition-colors duration-200 hover:border-amber-500/40 hover:bg-white/[0.06]"
                      data-testid={`walkthrough-cat-${c.key}`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <p className="truncate font-medium text-[#f5f0e8]">{c.label}</p>
                          {done[c.key] && <Check className="h-4 w-4 shrink-0 text-emerald-400" />}
                        </div>
                        <p className="mt-0.5 truncate text-xs text-[#f5f0e8]/50">{c.blurb}</p>
                        <p className="mt-1.5 text-[11px] uppercase tracking-wide text-amber-400/70">
                          {c.steps.length} steps
                        </p>
                      </div>
                      <ChevronRight className="h-4 w-4 shrink-0 text-[#f5f0e8]/30 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-amber-400" />
                    </button>
                  ))}
                </div>
              ))}
              {visible.length === 0 && (
                <p className="text-sm text-[#f5f0e8]/50" data-testid="walkthrough-empty">
                  Walkthroughs are being prepared — check back shortly.
                </p>
              )}
            </div>

            <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t border-white/10 pt-4">
              <p className="text-xs text-[#f5f0e8]/45">
                Close this and it will open again next time you are here, where you left off.
              </p>
              <button
                onClick={onFinish}
                className="text-xs text-[#f5f0e8]/55 underline decoration-dotted underline-offset-4 transition-colors duration-200 hover:text-[#f5f0e8]"
                data-testid="walkthrough-opt-out"
              >
                Don&apos;t show me this again
              </button>
            </div>
          </>
        ) : (
          <>
            <button
              onClick={() => { setOpenCat(null); setI(0); }}
              className="flex items-center gap-1.5 text-xs text-[#f5f0e8]/55 transition-colors duration-200 hover:text-[#f5f0e8]"
              data-testid="walkthrough-back-to-areas"
            >
              <ArrowLeft className="h-3.5 w-3.5" /> All areas
            </button>

            <div className="mt-3 flex flex-wrap items-baseline justify-between gap-2">
              <h2 className="font-serif text-xl text-[#f5f0e8]">{cat.label}</h2>
              <p className="text-xs uppercase tracking-wide text-amber-400/80"
                 data-testid="walkthrough-step-counter">
                Step {i + 1} of {cat.steps.length}
              </p>
            </div>

            <img
              src={step.image}
              alt={step.title}
              className="mt-3 w-full rounded-xl border border-white/10"
              data-testid="walkthrough-image"
            />

            <h3 className="mt-4 text-base font-semibold text-[#f5f0e8]" data-testid="walkthrough-title">
              {step.title}
            </h3>
            <p className="mt-1 text-sm leading-relaxed text-[#f5f0e8]/65" data-testid="walkthrough-body">
              {step.body}
            </p>

            <div className="mt-5 flex items-center justify-between gap-3">
              <button
                onClick={() => setI((v) => Math.max(v - 1, 0))}
                disabled={i === 0}
                className="flex items-center gap-1.5 rounded-full border border-white/10 px-4 py-2 text-sm text-[#f5f0e8]/80 transition-colors duration-200 hover:bg-white/5 disabled:opacity-35"
                data-testid="walkthrough-prev"
              >
                <ArrowLeft className="h-4 w-4" /> Back
              </button>

              <div className="hidden gap-1.5 sm:flex">
                {cat.steps.map((s, n) => (
                  <button
                    key={s.id}
                    onClick={() => setI(n)}
                    aria-label={`Step ${n + 1}`}
                    className={`h-1.5 rounded-full transition-all duration-200 ${
                      n === i ? 'w-6 bg-amber-400' : 'w-1.5 bg-white/20 hover:bg-white/40'}`}
                    data-testid={`walkthrough-dot-${n}`}
                  />
                ))}
              </div>

              {i < cat.steps.length - 1 ? (
                <button
                  onClick={() => setI(i + 1)}
                  className="flex items-center gap-1.5 rounded-full bg-gradient-to-r from-amber-500 to-amber-600 px-5 py-2 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90"
                  data-testid="walkthrough-next"
                >
                  Next <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                <button
                  onClick={finishCat}
                  className="flex items-center gap-1.5 rounded-full bg-emerald-600 px-5 py-2 text-sm font-medium text-white transition-opacity duration-200 hover:opacity-90"
                  data-testid="walkthrough-finish"
                >
                  <Check className="h-4 w-4" /> Done with {cat.label}
                </button>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
